import React, { useState } from 'react';
import MapView from '../Map/MapView';
import VehiclesView from './VehiclesView';
import SharedVehiclesView from './SharedVehiclesView';
import SubscriptionsView from './SubscriptionsView';
import BusSchedulesView from './BusSchedulesView';

// Garbage Alert components (replaced EnhancedSubscriptionsView)
import GarbageAlertsView from '../GarbageAlert/GarbageAlertsView';

import NotificationDebug from '../Debug/NotificationDebug';
import WebSocketDebug from '../Debug/WebSocketDebug';
import PerformanceMonitor from '../Debug/PerformanceMonitor';
import MapPerformanceMonitor from '../Debug/MapPerformanceMonitor';
import LiveUpdateTest from '../Debug/LiveUpdateTest';
import PushNotificationSettings from '../PushNotificationSettings';

/**
 * Dashboard Content Renderer
 * Renders the appropriate view based on active tab
 */
const DashboardContent = ({
  activeTab,
  setActiveTab,  // Add tab navigation function
  // Map props
  mapCenter,
  mapZoom,
  setMapCenter,
  setMapZoom,
  publicVehicles,
  userVehicles,
  sharedVehicles,
  busRoutes,
  selectedVehicleId,
  setSelectedVehicleId,
  onVehicleClick,
  onBusClick,
  subscribedBusesWithDistance,

  // Location handling props
  mapFilters,
  userLocation,
  onFilterChange,
  onAutoLocate,           // Centers map + sets user location
  onLocateMeClick,        // NEW: Dedicated "Locate Me" button handler
  onSetUserLocation,      // NEW: Only sets user location (no map centering)
  shouldAutoCenter,       // NEW: Control auto-centering
  setShouldAutoCenter,    // NEW: Reset auto-center flag

  setSubscribedBusesWithDistance,
  onRefreshData,
  // Other props
  subscriptions,
  routeSubscriptions,
  ads,
  notifications,
  setNotifications,
  enableSwipe,
  disableSwipe,

  pushNotificationStatus,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  sendTestPushNotification,
  currentUserId,
  currentUser  // Add current user prop
}) => {
  const [showCreateSubscriptionForm, setShowCreateSubscriptionForm] = useState(false);

  const handleCreateSubscription = () => {
    // Check if there are no pickup alerts - redirect to Smart Notifications
    if (!subscribedBusesWithDistance || subscribedBusesWithDistance.length === 0) {
      // Navigate to Smart Notifications tab instead of showing modal
      setActiveTab('enhanced-subs');
      return;
    }

    // If there are alerts, show the modal as usual
    setShowCreateSubscriptionForm(true);
  };

  const handleCloseSubscriptionForm = () => {
    setShowCreateSubscriptionForm(false);
  };

  const handleSubscriptionFormSubmit = (subscriptionData) => {
    setShowCreateSubscriptionForm(false);
    if (onRefreshData) {
      onRefreshData();
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'map':

        // Combine all vehicles into one array and filter based on mapFilters
        let allVehicles = [];

        if (mapFilters.showBuses) {
          allVehicles = [...allVehicles, ...publicVehicles.filter(v => v.vehicle_type === 'bus' || v.vehicle_type === 'truck')];
        }
        if (mapFilters.showVans) {
          allVehicles = [...allVehicles, ...publicVehicles.filter(v => v.vehicle_type === 'van')];
        }
        if (mapFilters.showPrivateVehicles) {
          // Private vehicles include: public non-bus/van/truck vehicles, user vehicles, and shared vehicles
          allVehicles = [...allVehicles, ...publicVehicles.filter(v => v.vehicle_type !== 'bus' && v.vehicle_type !== 'van' && v.vehicle_type !== 'truck'), ...userVehicles, ...sharedVehicles];
        }
        if (mapFilters.showUserVehicles && !mapFilters.showPrivateVehicles) {
          // My vehicles only include user-owned vehicles (only add if Private Vehicles is not already selected)
          allVehicles = [...allVehicles, ...userVehicles];
        }

        // Remove duplicates based on vehicle ID
        const uniqueVehicles = allVehicles.filter((vehicle, index, self) =>
          index === self.findIndex(v => v.id === vehicle.id)
        );


        return (
          <>


            <MapView
              vehicles={uniqueVehicles}
              publicVehicles={publicVehicles}
              userVehicles={userVehicles}
              sharedVehicles={sharedVehicles}
              selectedVehicleId={selectedVehicleId}
              mapCenter={mapCenter}
              mapZoom={mapZoom}
              userLocation={userLocation}
              subscribedBusesWithDistance={subscribedBusesWithDistance}
              mapFilters={mapFilters}
              onVehicleSelect={setSelectedVehicleId}
              onAutoLocate={onAutoLocate}           // Centers map + sets user location
              onLocateMeClick={onLocateMeClick}     // NEW: Dedicated "Locate Me" button handler
              onSetUserLocation={onSetUserLocation}  // NEW: Only sets user location
              onFilterChange={onFilterChange}
              shouldAutoCenter={shouldAutoCenter}     // NEW: Control auto-centering
              setShouldAutoCenter={setShouldAutoCenter} // NEW: Reset auto-center flag
              onCreateSubscription={handleCreateSubscription}
              enableSwipe={enableSwipe}
              disableSwipe={disableSwipe}
              onSubscribedBusClick={(bus) => {
                // Select the bus and center map on its location
                setSelectedVehicleId(bus.id);
                
                if (bus.last_location) {
                  const { latitude, longitude } = bus.last_location;
                  setMapCenter({ lat: latitude, lng: longitude });
                  setMapZoom(16); // Zoom in to show detailed view
                  setShouldAutoCenter(true); // Signal that auto-centering is wanted
                }
                
                // Scroll to the map to show the result
                setTimeout(() => {
                  const mapContainer = document.querySelector('.leaflet-container') ||
                    document.querySelector('[data-testid="map-container"]') ||
                    document.querySelector('.bg-white.rounded-lg.shadow'); // MapView container

                  if (mapContainer) {
                    mapContainer.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }
                }, 150);
              }}
            />
            {/* <NotificationDebug /> */}
          </>
        );

      case 'vehicles':
        return (
          <VehiclesView
            vehicles={userVehicles}
            onRefresh={onRefreshData}
            onVehicleClick={onVehicleClick || setSelectedVehicleId}
            enableSwipe={enableSwipe}
            disableSwipe={disableSwipe}
            currentUser={currentUser}  // Pass current user
          />

        );

      case 'shared':
        return (
          <SharedVehiclesView
            vehicles={sharedVehicles}
            onRefresh={onRefreshData}
            onVehicleClick={onVehicleClick || setSelectedVehicleId}
          />
        );

      case 'subscriptions':
        return (
          <SubscriptionsView
            subscriptions={subscriptions}
            routeSubscriptions={routeSubscriptions}
            busRoutes={busRoutes}
            onRefresh={onRefreshData}
            enableSwipe={enableSwipe}
            disableSwipe={disableSwipe}
          />
        );

      case 'enhanced-subs':
        return (
          <>
            <PushNotificationSettings
              pushNotificationStatus={pushNotificationStatus}
              subscribeToPushNotifications={subscribeToPushNotifications}
              unsubscribeFromPushNotifications={unsubscribeFromPushNotifications}
              sendTestPushNotification={sendTestPushNotification}
              currentUserId={currentUserId}
            />
            <GarbageAlertsView
              enableSwipe={enableSwipe}
              disableSwipe={disableSwipe}
            />
          </>
        );

      case 'schedules':
      case 'bus-schedules':
        //
        return (
          <BusSchedulesView />
        );

      case 'debug':
        return (
          <div className="space-y-6">
            <MapPerformanceMonitor
              totalVehicles={(publicVehicles?.length || 0) + (userVehicles?.length || 0) + (sharedVehicles?.length || 0)}
              visibleVehicles={(publicVehicles?.length || 0) + (userVehicles?.length || 0) + (sharedVehicles?.length || 0)}
              enableClustering={true}
            />
            <LiveUpdateTest
              publicVehicles={publicVehicles}
              userVehicles={userVehicles}
              sharedVehicles={sharedVehicles}
            />
            <PerformanceMonitor
              subscriptionCount={enhancedSubscriptions?.length || 0}
              busCount={publicVehicles?.filter(v => v.vehicle_type === 'bus' || v.vehicle_type === 'truck')?.length || 0}
            />
            <WebSocketDebug userId={currentUserId} />
            <NotificationDebug />
          </div>
        );

      default:
        return (
          <div className="text-center py-8">
            <p className="text-gray-500">Invalid tab selected</p>
          </div>
        );
    }
  };

  return (
    <>
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-4 md:py-6 pb-20 md:pb-6">
        {renderContent()}
      </main>

      {/* Enhanced Route Subscription Form Modal */}
      {showCreateSubscriptionForm && (
        <EnhancedRouteSubscriptionForm
          onClose={handleCloseSubscriptionForm}
          onSubmit={handleSubscriptionFormSubmit}
        />
      )}
    </>
  );
};

export default DashboardContent;